import React, { ReactElement, useMemo } from 'react';

import Widget from 'domain/entity/widget';
import { LayoutType, ConstraintsPerLayoutElement } from 'domain/entity/layout';
import { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import Grid from 'adapter/in/dashboards/components/Grid';
import { useGridView } from 'adapter/in/dashboards/containers/Grid/useGridView';

import StyledGridSize from 'adapter/in/dashboards/containers/Grid/style';
import { WidgetDataProxyContext } from 'use-case/widgets/data/read/readWidgetDataCriteria';

export type GridContainerProps = {
    dashboardId: string;
    isEditEnabled: boolean;
    editingLayout: boolean;
    gridLayouts: GridLayouts;
    allGridLayouts: GridLayouts;
    widgetMap: Map<string, Widget>;
    selectedLayoutType: LayoutType;
    setAllGridLayouts: React.Dispatch<React.SetStateAction<GridLayouts>>;
    widgetDataContext?: WidgetDataProxyContext;
};

const GridContainer = (props: GridContainerProps): ReactElement => {
    const {
        dashboardId,
        editingLayout,
        isEditEnabled,
        gridLayouts,
        allGridLayouts,
        selectedLayoutType,
        setAllGridLayouts,
        widgetMap,
        widgetDataContext,
    } = props;

    const wrongSizeElements = useMemo(() => {
        const wrongSizeGridElementsWorker: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>> = {
            [LayoutType.DESKTOP]: {},
            [LayoutType.TABLET]: {},
            [LayoutType.MOBILE]: {},
            [LayoutType.A4]: {},
        };
        if (Object.keys(allGridLayouts).length > 0) {
            Object.keys(allGridLayouts).forEach((layoutType) => {
                allGridLayouts[layoutType].forEach((gridLayoutElement) => {
                    if (!elementFitsContraints(gridLayoutElement)) {
                        wrongSizeGridElementsWorker[layoutType as LayoutType][gridLayoutElement.i] = {
                            type: layoutType as LayoutType,
                            minHeight: gridLayoutElement.minHeight,
                            minWidth: gridLayoutElement.minWidth,
                            maxHeight: gridLayoutElement.maxHeight,
                            maxWidth: gridLayoutElement.minWidth,
                        };
                    }
                });
            });
        }
        return wrongSizeGridElementsWorker;
    }, [allGridLayouts]);

    const {
        cellSize,
        backgroundStyle,
        backgroundColor,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        editLayoutWidthStyle,
        onDragStart,
        onDragStop,
        onResize,
        onResizeStop,
        onWidthChange,
        onBreakpointChange,
        onLayoutChange,
        widgetBeingResized,
    } = useGridView(gridLayouts, allGridLayouts, editingLayout, selectedLayoutType, isEditEnabled, setAllGridLayouts, wrongSizeElements);

    return (
        <StyledGridSize $editLayoutWidthStyle={editLayoutWidthStyle} $backgroundColor={backgroundColor}>
            <Grid
                dashboardId={dashboardId}
                widgetMap={widgetMap}
                gridLayouts={gridLayouts}
                isEditEnabled={isEditEnabled}
                cellSize={cellSize}
                backgroundStyle={backgroundStyle}
                currentBreakpoint={currentBreakpoint}
                breakpoints={breakpoints}
                containerWidth={containerWidth}
                onResize={onResize}
                onDragStart={onDragStart}
                onResizeStop={onResizeStop}
                onDragStop={onDragStop}
                onWidthChange={onWidthChange}
                onBreakpointChange={onBreakpointChange}
                onLayoutChange={onLayoutChange}
                wrongSizeElements={wrongSizeElements}
                widgetBeingResized={widgetBeingResized}
                widgetDataContext={widgetDataContext}
            />
        </StyledGridSize>
    );
};

export default GridContainer;

// Utils

export const elementFitsContraints = (layoutElement: GridLayoutElement): boolean =>
    layoutElement.h <= layoutElement.maxHeight &&
    layoutElement.w <= layoutElement.maxWidth &&
    layoutElement.h >= layoutElement.minHeight &&
    layoutElement.w >= layoutElement.minWidth;
